import * as KatalMetrics from '@amzn/katal-metrics';
import KatalMetricsDriverSushi from '@amzn/katal-metrics-driver-sushi';
import KatalMetricsDriverConsoleLogJson from '@amzn/katal-metrics/lib/driver/KatalMetricsDriverConsoleLogJson';
import KatalMetricsDriverArrayCollector from '@amzn/katal-metrics/lib/driver/KatalMetricsDriverArrayCollector';
import {InjectedConfig, METRICS_DOMAIN_VALUES} from 'src/config';

const metricsConsoleErrorHandler = (err: Error) => console.error(err);

const REGION_PATTERN = /^[a-z]{2}-[a-z]+-\d$/;

// Sushi realm is derived from the build-injected region so each realm's metrics
// are isolated in their own PMET namespace (US / EU / FE), keyed by region
// prefix. Falls back to USAmazon if the region token was not replaced at build
// time (that failure is separately surfaced by the config-validation metric).
const REGION_PREFIX_TO_REALM: {readonly [prefix: string]: string} = {
    us: 'USAmazon',
    eu: 'EUAmazon',
    ap: 'FEAmazon',
};
const resolveRealm = (): string =>
    REGION_PATTERN.test(InjectedConfig.region)
        ? (REGION_PREFIX_TO_REALM[InjectedConfig.region.split('-')[0]] ?? 'USAmazon')
        : 'USAmazon';

// Sushi domain is injected per stage (src/config -> {{Domain}}). Pass the
// injected value through when it is a recognized Katal domain (prod/master/test,
// per the FITUICDK Domain enum); otherwise fail safe to 'test' so a missing or
// unresolved token can never route pre-prod metrics to prod PMET.
const resolveDomain = (): string =>
    METRICS_DOMAIN_VALUES.includes(InjectedConfig.domain as string)
        ? (InjectedConfig.domain as string)
        : 'test';

// The build-time injected region (src/config) scopes each metric to its cell.
// Falls back to 'unknown' if the token was not replaced at build time -- that
// failure is separately surfaced by the config-validation metric.
const regionTag = (): string =>
    REGION_PATTERN.test(InjectedConfig.region) ? InjectedConfig.region : 'unknown';

const makeMetricsDriver = (): KatalMetrics.MetricsDriver => {
    if (process.env.NODE_ENV === 'test') {
        const metricsDriver = new KatalMetricsDriverArrayCollector();
        //  Attach to global window object so tests can see it
        (window as any).metricsDriver = metricsDriver;
        return metricsDriver;
    } else if (process.env.NODE_ENV !== 'production') {
        return new KatalMetricsDriverConsoleLogJson();
    } else {
        return new KatalMetricsDriverSushi.Builder()
            .withDomainRealm(resolveDomain(), resolveRealm())
            .withErrorHandler(metricsConsoleErrorHandler)
            .build();
    }
};

const makePublisher = (): KatalMetrics.Publisher => {

    const metricsDriver = makeMetricsDriver();
    const initialMetricsContext = new KatalMetrics.Context.Builder()
        .withSite('FITUIWebsite')
        .withServiceName('FITUI')
        .build();
    return new KatalMetrics.Publisher(
        metricsDriver,
        metricsConsoleErrorHandler,
        initialMetricsContext);
};

const initialMetricsPublisher = makePublisher();

/**
 * Metric method names (the PMET "MethodName" dimension). One per interactive
 * login-flow step the UI owns, plus cross-cutting error/config methods.
 */
export const MetricMethod = {
    IdPSelection: 'IdPSelection',
    DeviceVerification: 'DeviceVerification',
    JsError: 'JsError',
    Config: 'Config',
} as const;

/**
 * Step counters used to compute completion rate (Success / Start) per step.
 */
export const StepMetric = {
    Start: 'Start',
    Success: 'Success',
    Failure: 'Failure',
} as const;

/**
 * Emit a counter metric. Counts/events only -- never include tokens, auth
 * codes, device/user codes, redirect URLs, or any PII in the method or metric
 * name (this is an authentication surface). Failures to publish are swallowed:
 * a missing metric must never change what the user sees.
 */
export const emitCounter = (method: string, name: string, value = 1): void => {
    try {
        initialMetricsPublisher
            .newChildActionPublisherForMethod(`${method}.${regionTag()}`)
            .publishCounterMonitor(name, value);
    } catch (err) {
        metricsConsoleErrorHandler(err as Error);
    }
};

export default initialMetricsPublisher;
